import React from 'react';
import {
  Box,
  Text,
  TrashIcon,
  Dropdown,
  DropdownOverlay,
  ActionList,
  ActionListItem,
  ActionListItemIcon,
  DropdownButton,
} from '@razorpay/blade/components';
import { WorkflowStep, TriggerStepData, DecisionNode, ActionStepData, StepType } from '../../types';
import { StepCardWrapper } from './styles';
import { StepIcon } from './StepIcon';

interface StepCardProps {
  step?: WorkflowStep;
  stepNumber: number;
  type: StepType;
  label?: string;
  sublabel?: string;
  isSelected?: boolean;
  onClick?: () => void;
  onDelete?: () => void;
  isPlaceholder?: boolean;
  placeholderText?: string;
  showMenu?: boolean;
  hasHighlight?: boolean;
  isDeletable?: boolean;
}

const StepCard = ({
  step,
  stepNumber,
  type,
  label,
  sublabel,
  isSelected,
  onClick,
  onDelete,
  isPlaceholder,
  placeholderText,
  showMenu = true,
  hasHighlight,
  isDeletable = false,
}: StepCardProps) => {
  const getTriggerLabel = () => {
    if (label) return label;
    if (!step || type !== 'trigger') return placeholderText || 'Choose a starter';
    const triggerData = step.data as TriggerStepData;
    return triggerData.name || 'Choose a starter';
  };

  const getDecisionLabel = () => {
    if (label) return label;
    if (!step || type !== 'decision') return placeholderText || 'Decide';
    return 'Decide';
  };

  const getActionLabel = () => {
    if (label) return label;
    if (!step || type !== 'action') return placeholderText || 'Choose a step';
    const actionData = step.data as ActionStepData;
    return actionData.name || 'Choose a step';
  };

  const getLabel = () => {
    switch (type) {
      case 'trigger':
        return getTriggerLabel();
      case 'decision':
        return getDecisionLabel();
      case 'condition':
        return label || 'Check if';
      case 'action':
        return getActionLabel();
      default:
        return 'Unknown step';
    }
  };

  const getStepTypeLabel = () => {
    switch (type) {
      case 'trigger':
        return 'Starter';
      case 'decision':
        return 'Decide';
      case 'condition':
        return 'Check if';
      case 'action':
        return 'Action';
      default:
        return 'Step';
    }
  };

  const getActionCode = (): string | undefined => {
    if (type === 'action' && step) {
      return (step.data as ActionStepData)?.code;
    }
    return undefined;
  };

  const handleDeleteClick = () => {
    onDelete?.();
  };

  return (
    <StepCardWrapper 
      isSelected={isSelected} 
      isPlaceholder={isPlaceholder} 
      hasHighlight={hasHighlight}
      onClick={onClick}>
      <Box display="flex" alignItems="center" gap="spacing.3">
        <StepIcon type={type} actionCode={getActionCode()} />
        <Box display="flex" flexDirection="column" gap="spacing.0" flexGrow={1}>
          <Text size="small" color="surface.text.gray.muted">
            Step {stepNumber}: {sublabel || getStepTypeLabel()}
          </Text>
          <Text 
            weight={isPlaceholder ? 'regular' : 'semibold'} 
            color={isPlaceholder ? 'interactive.text.primary.normal' : undefined}>
            {getLabel()}
          </Text>
        </Box>
        {showMenu && step && !isPlaceholder && isDeletable && (
          <Dropdown>
            <DropdownButton
              variant="tertiary"
              size="small"
              icon={TrashIcon}
              accessibilityLabel="Delete step"
            />
            <DropdownOverlay>
              <ActionList>
                <ActionListItem
                  title="Delete"
                  value="delete"
                  leading={<ActionListItemIcon icon={TrashIcon} />}
                  onClick={handleDeleteClick}
                  intent="negative"
                />
                <ActionListItem
                  title="Edit"
                  value="edit"
                  onClick={onClick}
                />
              </ActionList>
            </DropdownOverlay>
          </Dropdown>
        )}
      </Box>
    </StepCardWrapper>
  );
};

export { StepCard };
